import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const TAX = 0.07;

const Checkout = () => {
  const { items, subtotal, clearCart } = useCart();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [days, setDays] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", address: "",
    fulfillment_type: "pickup", fulfillment_date: "", notes: "", redeem_reward: "",
  });

  useEffect(() => {
    client.get("/menu/availability").then((r) => {
      const open = r.data.days.filter((d) => d.orderable);
      setDays(open);
      if (open[0]) setForm((f) => ({ ...f, fulfillment_date: open[0].date }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, first_name: user.first_name, last_name: user.last_name, email: user.email, phone: user.phone || "" }));
      client.get("/rewards").then((r) => setRewards(r.data)).catch(() => {});
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const discount = (() => {
    if (!form.redeem_reward || !rewards) return 0;
    const t = rewards.tiers.find((x) => x.key === form.redeem_reward);
    const vals = { dessert: 5.99, sweets: 39.99 };
    return t ? Math.min(vals[form.redeem_reward], subtotal) : 0;
  })();
  const couponDiscount = coupon ? Math.round((subtotal - discount) * coupon.percent * 100) / 100 : 0;
  const taxable = Math.max(0, subtotal - discount - couponDiscount);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const r = await client.post("/coupons/validate", { code: couponInput });
      setCoupon(r.data);
      toast.success(`Coupon ${r.data.code} applied — ${r.data.percent * 100}% off`);
    } catch (err) {
      setCoupon(null);
      toast.error(err?.response?.data?.detail || "Invalid coupon code");
    }
  };
  const tax = Math.round(taxable * TAX * 100) / 100;
  const total = taxable + tax;

  if (items.length === 0) {
    return <div className="max-w-xl mx-auto px-4 pt-28 pb-24 text-center text-[#8a7458]">Your cart is empty. <button className="text-[#9a4a2f] font-semibold" onClick={() => nav("/menu")}>Browse menu</button></div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error("Name and email are required"); return; }
    if (!form.fulfillment_date) { toast.error("Please choose a day"); return; }
    if (form.fulfillment_type === "delivery" && !form.address.trim()) { toast.error("Delivery address is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, image: i.image || "" })),
        fulfillment_type: form.fulfillment_type,
        fulfillment_date: form.fulfillment_date,
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        phone: form.phone, address: form.address, notes: form.notes,
        redeem_reward: form.redeem_reward || null,
        coupon_code: coupon?.code || null,
      };
      const res = await client.post("/orders", payload);
      clearCart();
      if (user) refreshUser();
      nav("/order-success", { state: { order: res.data.order } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 pt-20 pb-28">
      <h1 className="font-heading text-3xl font-bold text-[#2c1e12]">Checkout</h1>
      <form onSubmit={submit} className="mt-5 space-y-5">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#2c1e12] mb-2">Pickup or delivery</h3>
          <div className="grid grid-cols-2 gap-2">
            {["pickup", "delivery"].map((t) => (
              <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, fulfillment_type: t }))}
                className={`rounded-xl py-2.5 text-sm font-medium capitalize transition-colors ${form.fulfillment_type === t ? "bg-[#9a4a2f] text-white" : "bg-[#efe3cd] text-[#4a3524]"}`}>{t}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#4a3524] mb-1">Order day</label>
          <select value={form.fulfillment_date} onChange={set("fulfillment_date")} className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40">
            {days.length === 0 && <option value="">No days available right now</option>}
            {days.map((d) => <option key={d.date} value={d.date}>{d.weekday}, {d.date}</option>)}
          </select>
          <p className="text-xs text-[#8a7458] mt-1">Orders must be placed before 10 PM the day before. No same-day orders.</p>
        </div>

        <div>
          <h3 className="font-heading text-lg font-bold text-[#2c1e12] mb-2">Your details</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" value={form.first_name} onChange={set("first_name")} />
            <Field label="Last name" value={form.last_name} onChange={set("last_name")} />
          </div>
          <Field label="Email" type="email" value={form.email} onChange={set("email")} />
          <Field label="Phone number" value={form.phone} onChange={set("phone")} />
          {form.fulfillment_type === "delivery" && (
            <Field label="Delivery address" value={form.address} onChange={set("address")} placeholder="Street, city, ZIP" />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#4a3524] mb-1">Order notes</label>
          <textarea rows={2} value={form.notes} onChange={set("notes")} placeholder="Allergies, preferences, instructions…" className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40" />
        </div>

        {rewards && rewards.tiers.some((t) => t.available) && (
          <div>
            <label className="block text-sm font-medium text-[#4a3524] mb-1">Redeem a reward (optional)</label>
            <select value={form.redeem_reward} onChange={set("redeem_reward")} className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40">
              <option value="">No reward</option>
              {rewards.tiers.filter((t) => t.available).map((t) => (
                <option key={t.key} value={t.key}>{t.label} ({t.points} pts)</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-[#4a3524] mb-1">Coupon code</label>
          <div className="flex gap-2">
            <input data-testid="coupon-input" value={couponInput} onChange={(e) => { setCouponInput(e.target.value); setCoupon(null); }} placeholder="Enter coupon"
              className="flex-1 rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40" />
            <button type="button" data-testid="coupon-apply-btn" onClick={applyCoupon} className="rounded-xl bg-[#2c1e12] hover:bg-[#4a3524] text-white text-sm font-semibold px-5 transition-colors">Apply</button>
          </div>
          {coupon && <p data-testid="coupon-applied-msg" className="text-xs text-[#2c6e49] mt-1">{coupon.code} applied — {coupon.percent * 100}% off</p>}
        </div>

        <div className="rounded-2xl bg-[#f7e4df] p-4 space-y-2">
          <div className="flex justify-between text-[#6b5844]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          {discount > 0 && <div className="flex justify-between text-[#2c6e49]"><span>Reward discount</span><span>-${discount.toFixed(2)}</span></div>}
          {couponDiscount > 0 && <div data-testid="coupon-discount" className="flex justify-between text-[#2c6e49]"><span>Coupon ({coupon.code})</span><span>-${couponDiscount.toFixed(2)}</span></div>}
          <div className="flex justify-between text-[#6b5844]"><span>Tax (7%)</span><span data-testid="checkout-tax">${tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-lg text-[#2c1e12] pt-1 border-t border-[#e9c9c0]"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
          <p className="text-xs text-[#8a7458]">Pay via Zelle — instructions are shown after you order and in your confirmation email.</p>
        </div>

        <button disabled={submitting || days.length === 0} className="w-full rounded-xl bg-[#9a4a2f] hover:bg-[#853e26] disabled:opacity-60 text-white font-semibold py-3.5 transition-colors">
          {submitting ? "Placing order…" : "Place Order"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, type = "text", ...props }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-[#4a3524] mb-1">{label}</label>
    <input type={type} {...props} className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40" />
  </div>
);

export default Checkout;
